import PropTypes from 'prop-types';
import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import { Report } from 'types/report';
import { Account } from 'types/account';
import EmploymentVerification from './Screenings/EmploymentVerification';
import EducationVerification from './Screenings/EducationVerification';
import MotorVehicleReport from './Screenings/MotorVehicleReport';
import NationalCriminalSearch from './Screenings/NationalCriminalSearch';
import PersonalReferenceVerification from './Screenings/PersonalReferenceVerification';
import ProfessionalReferenceVerification from './Screenings/ProfessionalReferenceVerification';
import SSNTrace from './Screenings/SSNTrace';
import AbuseRegistrySearch from './Screenings/AbuseRegistrySearch';
import SafetyAllianceSearch from './Screenings/SafetyAllianceSearch';
import SocialMediaScreenings from './Screenings/SocialMediaScreenings';
import FacisVerification from './Screenings/FacisVerification';
import HealthcareSanctionsSearch from './Screenings/HealthcareSanctionsSearch';
import FederalCivilSearch from './Screenings/FederalCivilSearch';
import FederalDistrictCivilSearch from './Screenings/FederalDistrictCivilSearch';
import FederalCriminalSearch from './Screenings/FederalCriminalSearch';
import FederalDistrictCriminalSearch from './Screenings/FederalDistrictCriminalSearch';

import Search from './Search/Search';
import IntlGlobalWatchlistSearch from './Screenings/International/IntlGlobalWatchlistSearch';
import SearchCollection from './Search/SearchCollection';
import SexOffenderSearch from './Screenings/SexOffenderSearch';

import CandidateStories from './Screenings/CandidateStories';
import IntlAdverseMediaSearches from './Screenings/International/IntlAdverseMediaSearches';
import IntlCriminalSearchesV2 from './Screenings/International/IntlCriminalSearchesV2';
import IntlEducationVerification from './Screenings/International/IntlEducationVerification';
import IntlEmploymentVerification from './Screenings/International/IntlEmploymentVerification';
import GovernmentDocumentAuthentication from './Screenings/International/GovernmentDocumentAuthentication';
import IntlCanadaBcEnhancedCriminalSearch from './Screenings/International/IntlCanadaBcEnhancedCriminalSearch';
import IntlRightToWork from './Screenings/International/IntlRightToWork';
import IntlMotorVehicleReports from './Screenings/International/IntlMotorVehicleReports';
import { onlySSN } from '../../../lib/helpers';
import { getStatusTypeAndReportLevelStatuses } from './utils';
import { getSkippedCountries } from './lib/skippedScreenings';

// Backend screening classes this screening renders, and so whose country
// level skip events it owns.
const SKIPPED_SCREENING_TYPES = [
  'InternationalCriminalSearchV2',
  'MatrixInternationalCriminalSearch',
];

type SearchType = {
  status: string;
};

const filterByNonCanceled = (array: Array<SearchType> = []) => {
  return array.filter(search => search.status !== 'canceled');
};

interface CriminalScreeningsProps {
  report: Report;
  // The PDF payload carries the account alongside the report rather than on it.
  account?: Account;
  candidateFacingStatusEnabled?: boolean;
}
const CriminalScreenings = ({
  report,
  account,
  candidateFacingStatusEnabled = false,
}: CriminalScreeningsProps) => {
  const { statusType: reportStatusType } =
    getStatusTypeAndReportLevelStatuses(report);
  const reportAccount = account || report.account;
  const skippedCountries = getSkippedCountries(
    report.skippedScreenings,
    SKIPPED_SCREENING_TYPES,
  );

  return (
    <>
      {!onlySSN(report) && (
        <SSNTrace search={report.ssnTrace} report={report} />
      )}
      <Search
        dataTestId='arrest-searches'
        type='arrest'
        title={i18n.getStr('components.criminalScreenings.arrestSearch')}
        search={report.arrestSearch}
        assessmentEnabled
      />
      <Search
        dataTestId='civil-searches'
        type='eviction'
        title={i18n.getStr('components.criminalScreenings.civilSearch')}
        search={report.evictionSearch}
      />
      <SafetyAllianceSearch search={report.safetyAllianceSearch} />
      <SexOffenderSearch search={report.sexOffenderSearch} />
      <Search
        dataTestId='global-watchlist-searches'
        type='criminal'
        title={i18n.getStr(
          'components.criminalScreenings.globalWatchlistSearch',
        )}
        search={report.terroristWatchlistSearch}
        assessmentEnabled
      />
      <HealthcareSanctionsSearch
        searches={report.healthcareSanctionsSearches}
      />
      <SearchCollection
        dataTestId='international-criminal-searches'
        type='criminal'
        title={i18n.getStr(
          'components.criminalScreenings.internationalSearches',
        )}
        searches={report.internationalCriminalSearches}
      />
      <NationalCriminalSearch
        type='criminal'
        search={report.nationalCriminalSearch}
      />
      <SearchCollection
        dataTestId='state-searches'
        type='criminal'
        title={i18n.getStr('components.criminalScreenings.stateSearch')}
        hideSuspended
        searches={report.stateCriminalSearches}
        assessmentEnabled
      />
      <SearchCollection
        type='criminal'
        title={i18n.getStr('components.criminalScreenings.pointerStateSearch')}
        searches={report.pointerStateCriminalSearches}
      />
      <FederalCriminalSearch
        search={report.federalCriminalSearch}
        districtSearches={report.federalDistrictCriminalSearches}
        reportStatusType={reportStatusType}
      />
      <FederalDistrictCriminalSearch
        searches={report.federalDistrictCriminalSearches}
      />
      <FederalCivilSearch
        search={report.federalCivilSearch}
        districtSearches={report.federalDistrictCivilSearches}
        reportStatusType={reportStatusType}
      />
      <FederalDistrictCivilSearch
        searches={report.federalDistrictCivilSearches}
      />
      <SearchCollection
        dataTestId='county-criminal-searches'
        type='criminal'
        title={i18n.getStr('components.criminalScreenings.countySearches')}
        hideSuspended
        searches={filterByNonCanceled(report.countyCriminalSearches)}
        assessmentEnabled
        candidateFacingStatusEnabled={candidateFacingStatusEnabled}
      />
      <SearchCollection
        dataTestId='county-civil-searches'
        type='civil'
        title={i18n.getStr('components.criminalScreenings.countyCivilSearches')}
        searches={filterByNonCanceled(report.countyCivilSearches)}
      />
      <SearchCollection
        dataTestId='municipal-searches'
        type='criminal'
        title={i18n.getStr('components.criminalScreenings.municipalSearches')}
        searches={filterByNonCanceled(report.municipalCriminalSearches)}
        assessmentEnabled
      />
      <SearchCollection
        type='childAbuse'
        title={i18n.getStr('components.criminalScreenings.childAbuseSearch')}
        hideSuspended
        searches={filterByNonCanceled(report.childAbuseSearches)}
      />
      <IntlGlobalWatchlistSearch
        search={report.internationalGlobalWatchlistSearch}
      />
      <IntlAdverseMediaSearches
        searches={filterByNonCanceled(report.internationalAdverseMediaSearches)}
      />
      <IntlCriminalSearchesV2
        searches={filterByNonCanceled(report.internationalCriminalSearchesV2)}
        skippedCountries={skippedCountries}
        accountId={reportAccount?.id}
      />
      <IntlEducationVerification
        search={report.internationalEducationVerification}
      />
      <IntlEmploymentVerification
        search={report.internationalEmploymentVerification}
      />
      <GovernmentDocumentAuthentication
        search={report.internationalIdentityDocumentValidation}
      />
      <IntlCanadaBcEnhancedCriminalSearch
        search={report.internationalCanadaBcEnhancedCriminalSearch}
      />
      <IntlRightToWork search={report.internationalRightToWork} />
      <IntlMotorVehicleReports
        searches={report.internationalMotorVehicleReports}
      />
      <MotorVehicleReport search={report.motorVehicleReport} />
      <EmploymentVerification search={report.employmentVerification} />
      <EducationVerification search={report.educationVerification} />
      <PersonalReferenceVerification
        searches={filterByNonCanceled(report.personalReferenceVerifications)}
      />
      <ProfessionalReferenceVerification
        searches={filterByNonCanceled(
          report.professionalReferenceVerifications,
        )}
      />
      <AbuseRegistrySearch search={report.abuseRegistrySearch} />
      <SocialMediaScreenings search={report.socialMediaScreenings} />
      <FacisVerification search={report.facisSearch} />
      {report.account && (
        <CandidateStories
          companyName={report.account.name}
          stories={report.candidateStories || []}
          candidate={report.candidate}
        />
      )}
    </>
  );
};

CriminalScreenings.propTypes = {
  report: PropTypes.object.isRequired,
};

export default CriminalScreenings;
